import { useState } from 'preact/hooks'
import { websApi, type WebViewDto } from '@/api/webs'
import { useToast } from '@/hooks/useToast'
import { parseValidationErrors } from '@/utils/errorHandling'
import { retryOnStaleETag } from '@/utils/etagRetry'

interface UseWebSignOffParams {
    workspaceId: string
    campaignId: string
    webId: string
    web: WebViewDto | null
    setWeb: (updater: WebViewDto | ((prev: WebViewDto | null) => WebViewDto | null)) => void
    hasBigMarker: boolean
}

/** Shape returned by websApi mutations — narrow enough for the retry helper to inspect. */
type WebMutationResponse = { data?: WebViewDto; error?: string; status?: number }

export function useWebSignOff({ workspaceId, campaignId, webId, web, setWeb, hasBigMarker }: UseWebSignOffParams) {
    const { showError, showSuccess } = useToast()

    const [showSignOffModal, setShowSignOffModal] = useState(false)
    const [showReopenModal, setShowReopenModal] = useState(false)
    const [signingOff, setSigningOff] = useState(false)
    const [reopening, setReopening] = useState(false)

    // Sign-off and reopen are intents ("I want this signed off"), so a 412 retries on the fresh web.
    const attemptWithEtagRetry = (
        attempt: (etag: string) => Promise<WebMutationResponse>,
        currentETag: string
    ): Promise<WebMutationResponse> => retryOnStaleETag<{ ETag: string }, WebMutationResponse>(
        document => attempt(document.ETag),
        { ETag: currentETag },
        async () => {
            const fresh = await websApi.getById(workspaceId, webId)
            if (fresh.error || !fresh.data) return null
            setWeb(fresh.data)
            return fresh.data
        },
    )

    /**
     * @param slug The public address confirmed in the sign-off panel (ORB-482). Sent so the server
     * validates and commits the value the user just looked at. Omitted for a web whose slug is already
     * frozen, where there is nothing to choose.
     */
    const handleSignOff = async (slug?: string) => {
        if (!web) return
        setSigningOff(true)
        const res = await attemptWithEtagRetry(
            etag => websApi.signOff(workspaceId, campaignId, webId, { ETag: etag, Confirmed: true, Slug: slug }),
            web.ETag
        )

        if (!res.error && res.data) {
            setWeb(res.data)
            showSuccess('Signed off', hasBigMarker ? 'Web has been signed off and pushed to BigMarker.' : 'Web has been signed off.')
            setShowSignOffModal(false)
        } else if (res.status === 422) {
            // 422s were silent on purpose: the readiness chip listed every reason and the Sign Off
            // button stayed disabled while any stood, so one was near-unreachable. The panel now
            // collects the slug, a taken or malformed one is rejected here, and the panel renders no
            // server detail — silence would leave the user pressing a button that does nothing
            // (ORB-482). Surfaced only when we sent a slug; a readiness 422 is still the chip's story.
            if (slug !== undefined) {
                const detail = parseValidationErrors(res).map(e => e.Message).join(' ')
                showError('Sign-off failed', detail || 'That address could not be used. Try another one.')
            }
        } else {
            showError('Sign-off failed', res.error || 'Unable to sign off web')
        }
        setSigningOff(false)
    }

    const handleReopen = async () => {
        if (!web) return
        setReopening(true)
        const res = await attemptWithEtagRetry(
            etag => websApi.reopen(workspaceId, campaignId, webId, { ETag: etag }),
            web.ETag
        )

        if (!res.error && res.data) {
            setWeb(res.data)
            showSuccess('Reopened', 'Web has been reopened for editing.')
            setShowReopenModal(false)
        } else if (res.status !== 422) {
            showError('Reopen failed', res.error || 'Unable to reopen web')
        }
        setReopening(false)
    }

    return {
        showSignOffModal,
        setShowSignOffModal,
        showReopenModal,
        setShowReopenModal,
        handleSignOff,
        handleReopen,
        signingOff,
        reopening,
    }
}
